import { useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useDrag, useSpring } from "./Bars3D";
import { clamp01, ease, glowTex } from "./geo";

const DEF = [-0.35, 0.35];

function RingScene({ pct, drag }) {
  const group = useRef();
  const arc = useRef();
  const head = useRef();
  const prog = useRef(-1);
  const t0 = useRef(null);
  useSpring(group, drag, DEF);
  const dust = useMemo(() => {
    const p = new Float32Array(900 * 3);
    for (let i = 0; i < 900; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = 1.6 + (Math.random() - 0.5) * 0.9;
      p.set([Math.cos(a) * r, Math.sin(a) * r, (Math.random() - 0.5) * 0.4], i * 3);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(p, 3));
    return g;
  }, []);
  useFrame((s) => {
    if (t0.current === null) t0.current = s.clock.elapsedTime;
    const p = ease(clamp01((s.clock.elapsedTime - t0.current - 0.6) / 2.2));
    if (Math.abs(p - prog.current) > 0.004 || (p === 1 && prog.current !== 1)) {
      const a = Math.max(0.001, p * pct * Math.PI * 2);
      arc.current.geometry.dispose();
      arc.current.geometry = new THREE.TorusGeometry(1.6, 0.2, 32, 220, a);
      head.current.position.set(Math.cos(a) * 1.6, Math.sin(a) * 1.6, 0);
      prog.current = p;
    }
  });
  return (
    <group ref={group}>
      <group rotation={[0, Math.PI, Math.PI / 2]}>
        <mesh>
          <torusGeometry args={[1.6, 0.09, 16, 220]} />
          <meshStandardMaterial color="#3a2c22" transparent opacity={0.55} roughness={0.6} />
        </mesh>
        <mesh ref={arc}>
          <torusGeometry args={[1.6, 0.2, 32, 220, 0.001]} />
          <meshStandardMaterial color="#ffb347" emissive="#ff6a1a" emissiveIntensity={0.9} roughness={0.25} metalness={0.4} />
        </mesh>
        <sprite ref={head} scale={[0.9, 0.9, 1]}>
          <spriteMaterial map={glowTex()} color="#ffcf8a" blending={THREE.AdditiveBlending} transparent depthWrite={false} />
        </sprite>
      </group>
      <points geometry={dust}>
        <pointsMaterial size={0.035} map={glowTex()} color="#ff9a4a" transparent opacity={0.6} depthWrite={false} blending={THREE.AdditiveBlending} />
      </points>
    </group>
  );
}

export function Ring3D({ pct, children, testid }) {
  const [drag, handlers] = useDrag(DEF);
  return (
    <div className="chart3d ring3d" data-testid={testid} data-nonav {...handlers}>
      <Canvas camera={{ position: [0, 0, 6], fov: 40 }} dpr={[1, 2]} gl={{ antialias: true, alpha: true }} resize={{ offsetSize: true }}>
        <ambientLight intensity={0.4} />
        <directionalLight position={[3, 5, 6]} intensity={2} color="#ffd8b0" />
        <pointLight position={[-4, -2, 3]} intensity={30} color="#6cc5ff" />
        <RingScene pct={pct} drag={drag} />
      </Canvas>
      <div className="ring-center">{children}</div>
    </div>
  );
}
